import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { HiOutlineDocumentText } from "react-icons/hi";
import { useQueryParams } from "../hooks/useQueryParams";
import useFetch from "../hooks/useFetch";
import { TailSpin } from "react-loader-spinner";
import CustomTable from "./ui/DataTable";
import { useSelector } from "react-redux";
import { selectCustomPagination } from "../features/paginationStore";
import Pagination from "./ui/Pagination";
import { selectCid } from '../features/companyid';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
// Dummy candidates removed in favor of direct API nested fetches.

const DarkCandidateTooltip = ({ children, candidate, className }) => {
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [tooltipHeight, setTooltipHeight] = useState(0);
    const tooltipRef = useRef(null);

    const handleMouseMove = (e) => {
        setPos({ x: e.clientX, y: e.clientY });
    };

    useLayoutEffect(() => {
        if (visible && tooltipRef.current) {
            setTooltipHeight(tooltipRef.current.offsetHeight);
        }
    }, [visible, candidate]);

    const isRightOverflow = pos.x + 420 > window.innerWidth;

    // Center vertically on cursor and clamp to screen
    let top = pos.y - (tooltipHeight / 2);
    const padding = 20;
    if (top < padding) top = padding;
    if (top + tooltipHeight > window.innerHeight - padding) top = window.innerHeight - tooltipHeight - padding;

    const displayedSkills = (candidate.skills_matched || []).slice(0, 4);
    const remainingSkillsCount = (candidate.skills_matched || []).length - 4;

    return (
        <div
            className={className || "relative inline-flex items-center justify-center cursor-pointer"}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onMouseMove={handleMouseMove}
        >
            {children}
            {visible && (
                <div
                    ref={tooltipRef}
                    className="fixed z-[100] p-4 rounded-lg shadow-xl pointer-events-none flex flex-col gap-3"
                    style={{
                        backgroundColor: "#0F172A",
                        width: "400px",
                        top: top,
                        left: pos.x,
                        transform: `translateX(${isRightOverflow ? "calc(-100% - 15px)" : "15px"})`
                    }}
                >
                    <div className="flex justify-between items-center text-white">
                        <span className="font-semibold text-lg">{candidate.name}</span>
                    </div>
                    <div className="text-gray-300 text-xs leading-relaxed">
                        {candidate.summary}
                    </div>
                    <div className="flex flex-col gap-2 text-xs mt-2">
                        <div className="flex justify-between items-center text-gray-400">
                            <span>Experience</span>
                            <span className="text-white">{candidate.total_experience ? `${candidate.total_experience}` : '-'}</span>
                        </div>
                        <div className="flex justify-between items-center text-gray-400">
                            <span>Highest Education</span>
                            <span className="text-white">{candidate.highest_education || '-'}</span>
                        </div>
                        <div className="flex justify-between items-center text-gray-400">
                            <span>Match Score</span>
                            <span className="text-white font-medium text-sm">{candidate.match_score ? `${candidate.match_score}/10` : '-'}</span>
                        </div>
                        <div className="flex gap-4 mt-2">
                            <span className="text-gray-400 w-16">Reason</span>
                            <span className="text-gray-300 flex-1 leading-relaxed text-right">{candidate.reason_for_fit}</span>
                        </div>
                        <div className="flex gap-4 mt-2 items-center">
                            <span className="text-gray-400 w-16">Skills</span>
                            <div className="flex flex-wrap gap-1 flex-1 justify-end">
                                {displayedSkills.map((skill, i) => (
                                    <span key={i} className="bg-white/10 text-gray-300 px-2 py-1 rounded text-xs">{skill}</span>
                                ))}
                                {remainingSkillsCount > 0 && (
                                    <span className="bg-white/10 text-gray-300 px-2 py-1 rounded text-xs">+{remainingSkillsCount}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const handleCandidateClick = (candidate) => {
    window.open(candidate.resume_url, "_blank");
}

const ExpandedJobCandidates = ({ jobId }) => {
    const pageKey = `job_candidates_${jobId}`;
    const cid = useSelector(selectCid);
    const { page, page_size } = useSelector((state) => selectCustomPagination(state, pageKey));

    const { data, isLoading, refetch } = useFetch(
        `/api/v1/internal-sourcing/job-candidates?company_id=${cid}&job_id=${jobId}&page=${page}&page_size=${page_size}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    const [deletingUuid, setDeletingUuid] = useState(null);
    const [addingUuid, setAddingUuid] = useState(null);

    const { send: addToApplicant } = useFetch(
        '/api/v1/internal-sourcing/add-to-applicant',
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        false
    );

    const { send: rejectCandidate } = useFetch(
        '/api/v1/internal-sourcing/reject-candidate',
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        false
    );

    const handleAddToApplication = async (candidate) => {
        setAddingUuid(candidate.candidate_uuid);
        try {
            const res = await addToApplicant({
                job_id: jobId,
                candidate_uuid: candidate.candidate_uuid,
                company_id: cid,
            });
            if (res?.status === 'added_to_applicant') {
                toast.success('Candidate added to application');
                refetch();
            } else {
                toast.error(res?.error || 'Failed to add candidate');
            }
        } catch (err) {
            toast.error('An error occurred');
        } finally {
            setAddingUuid(null);
        }
    };

    const handleDelete = async (candidate) => {
        setDeletingUuid(candidate.candidate_uuid);
        try {
            const res = await rejectCandidate({
                job_id: jobId,
                candidate_uuid: candidate.candidate_uuid,
                company_id: cid,
            });
            if (res?.status === 'rejected') {
                toast.success('Recommendation removed');
                refetch();
            } else {
                toast.error(res?.error || 'Failed to remove recommendation');
            }
        } catch (err) {
            toast.error('An error occurred');
        } finally {
            setDeletingUuid(null);
        }
    }

    const candidates = data?.candidates || [];
    const totalPages = data?.pagination?.total_pages || 1;

    const candidateColumns = [
        {
            key: "candidates",
            header: <div className="text-left ml-4">Candidates</div>,
            notCentered: true,
            width: "25%",
            body: (row) => (
                <div className="flex items-center text-left ml-4" onClick={() => handleCandidateClick(row)}>
                    <DarkCandidateTooltip candidate={row}>
                        <span className="flex items-center gap-1.5 font-medium text-web-blue whitespace-nowrap cursor-pointer hover:underline">
                            {row.name || "Anonymous"} <HiOutlineDocumentText className="text-xl" />
                        </span>
                    </DarkCandidateTooltip>
                </div>
            )
        },
        {
            key: "matchScore",
            header: "Match Score",
            width: "15%",
            body: (row) => {
                const score = parseFloat(row.match_score) || 0;
                return (
                    <span className={`font-medium ${score >= 7.5 ? 'text-green-500' : score >= 5 ? 'text-primaryBlack' : 'text-red-500'}`}>
                        {row.match_score}/10
                    </span>
                );
            }
        },
        {
            key: "skills",
            header: <div className="text-left ml-2">Skills matched</div>,
            notCentered: true,
            width: "30%",
            body: (row) => (
                <div className="flex flex-wrap gap-1 justify-start ml-2">
                    {row.skills_matched && row.skills_matched.map((skill, i) => (
                        <span key={i} className="bg-blue-50 text-blue-500 px-2 py-1 rounded text-xs font-medium">{skill}</span>
                    ))}
                </div>
            )
        },
        {
            key: "actions",
            header: "",
            width: "30%",
            body: (row) => (
                <div className="flex items-center justify-end gap-3 mr-4">
                    <button
                        onClick={() => handleAddToApplication(row)}
                        disabled={addingUuid === row.candidate_uuid || deletingUuid === row.candidate_uuid}
                        className="px-5 py-2 border border-green-500 bg-green-500 rounded-md text-white text-xs font-semibold hover:bg-green-600 transition-colors shadow-sm whitespace-nowrap disabled:opacity-50 min-w-[120px] flex justify-center"
                    >
                        {addingUuid === row.candidate_uuid ? (
                            <TailSpin color="white" height={16} width={16} />
                        ) : 'Add to application'}
                    </button>
                    <button
                        onClick={() => handleDelete(row)}
                        disabled={deletingUuid === row.candidate_uuid || addingUuid === row.candidate_uuid}
                        className="px-5 py-2 border border-red-200 bg-red-50 rounded-md text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] flex justify-center"
                    >
                        {deletingUuid === row.candidate_uuid ? (
                            <TailSpin color="#ef4444" height={16} width={16} />
                        ) : (
                            <FaTimes className='w-4 h-4' />
                        )}
                    </button>
                </div>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="w-full flex justify-center py-10 border-t border-gray-200 bg-gray-50/30">
                <TailSpin color="blue" height={40} width={40} />
            </div>
        );
    }

    return (
        <div className="border-t border-gray-200 bg-gray-50/30">
            <CustomTable
                columns={candidateColumns}
                tableData={candidates}
                noBorder={true}
                className="!px-0"
                tableClass="!my-0 !rounded-none"
            />
            {candidates.length > 0 && (
                <div className="bg-white px-2 pt-2 pb-4">
                    <Pagination pageKey={pageKey} totalPages={totalPages} className="!bg-transparent" />
                </div>
            )}
        </div>
    );
};

const JobRecommendationsTable = () => {
    const [expandedJobId, setExpandedJobId] = useState(1);

    const pageKey = "job_recommendations";
    const { page, page_size } = useSelector((state) => selectCustomPagination(state, pageKey));
    const { query, setQueryParams } = useQueryParams();
    const cid = useSelector(selectCid);

    useEffect(() => {
        if (query.page !== String(page) || query.page_size !== String(page_size)) {
            setQueryParams({ page: String(page), page_size: String(page_size) });
        }
    }, [page, page_size, query.page, query.page_size, setQueryParams]);

    const { data, isLoading } = useFetch(
        `/api/v1/internal-sourcing/jobs` +
        `?company_id=${cid}` +
        `&page=${page}` +
        `&page_size=${page_size}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    const jobs = data?.jobs || [];
    const totalPages = data?.pagination?.total_pages || 1;

    return (
        <div className="w-full flex flex-col mt-4">
            {isLoading ? (
                <div className="w-full flex justify-center py-20">
                    <TailSpin color="blue" height={60} width={60} />
                </div>
            ) : jobs.length > 0 ? (
                jobs.map((job) => {
                    const isExpanded = expandedJobId === job.job_id;

                    return (
                        <div key={job.job_id} className="border border-gray-200 rounded-xl mb-4 bg-white overflow-hidden shadow-sm">
                            {/* Job Header */}
                            <div className="p-5 flex flex-col gap-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <span className="font-bold text-lg text-primaryBlack mb-0.5">{job.title}</span>
                                        <span className="text-sm text-gray-400 flex gap-2"><span className='text-primaryBlack'>{job.recommended_candidate_count} Applicants </span>| {job.department}</span>
                                    </div>
                                    <button
                                        onClick={() => setExpandedJobId(isExpanded ? null : job.job_id)}
                                        className="px-6 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-primaryBlack hover:bg-gray-50 transition-colors"
                                    >
                                        {isExpanded ? "Hide" : "Expand"}
                                    </button>
                                </div>

                                <div className="grid grid-cols-3 gap-6 text-sm text-gray-500 max-w-4xl">
                                    <div className="flex flex-col gap-1.5 whitespace-nowrap">
                                        <div><span className="text-gray-400 mr-1">No of openings :</span> <span className="text-primaryBlack font-medium">{job.no_of_openings}</span></div>
                                        <div><span className="text-gray-400 mr-1">Job type :</span> <span className="text-primaryBlack font-medium">{job.job_type}</span></div>
                                    </div>
                                    <div className="flex flex-col gap-1.5 whitespace-nowrap">
                                        <div><span className="text-gray-400 mr-1">Location :</span> <span className="text-primaryBlack font-medium">{job.location}</span></div>
                                        <div><span className="text-gray-400 mr-1">Experience :</span> <span className="text-primaryBlack font-medium">{job.experience_min}-{job.experience_max} years</span></div>
                                    </div>
                                    <div className="flex flex-col gap-1.5 whitespace-nowrap">
                                        <div><span className="text-gray-400 mr-1">Recruiter :</span> <span className="text-primaryBlack font-medium">{job.recruiter_name || "-"}</span></div>
                                        <div><span className="text-gray-400 mr-1">Hiring Manager :</span> <span className="text-primaryBlack font-medium">{job.hiring_manager_name || "-"}</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Job Candidates fetching component */}
                            {isExpanded && <ExpandedJobCandidates jobId={job.job_id} />}
                        </div>
                    );
                })
            ) : (
                <div className="text-center py-10 text-gray-500 italic">No recommended jobs found.</div>
            )}

            <Pagination pageKey={pageKey} totalPages={totalPages} className="!bg-transparent" />
        </div>
    );
};

export default JobRecommendationsTable;
